import * as React from 'react'
import { createContext, PropsWithChildren, ReactElement, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { useStore } from 'react-redux'
import useEntity from '../hooks/useEntity'
import { DataPromise, getDataEmptyState } from './AxiosContext'
import { SortDirection, SortSetting } from '../config/settingConfig'
import { ComboBoxOption, sortOptions } from '../components/Dropdown/BaseDropdown'
import useAxios from '../hooks/useAxios'
import AuthorizationContext from './AuthorizationContext'
import { Store } from '../reducers'
import { createFileDocumentStore, FileDocumentStore } from './fileDocumentStore'
// Type-only: DocumentFlatTree imports this context at runtime, so a value import would close the cycle.
import type { FolderDriveItem } from '../components/Document/DocumentFlatTree'

export enum FileStatus {
    Open = 'OPEN',
    Archived = 'ARCHIVED',
}

export type FileListItemDto = {
    id: string
    name: string
    normalizedName: string
    reference: string
    status: FileStatus
    documentRootId: string
    kyc: boolean
}

/** The little a screen needs to know about a dossier: enough for links, subjects and its document root. */
export type FileReference = Pick<FileListItemDto, 'id' | 'reference' | 'documentRootId'>

export type FileContextType = {
    files: DataPromise<FileListItemDto[]>
    resetFiles: () => void
    upsertFile: (file: FileListItemDto) => void
    validateReferences: (references: string[]) => Promise<Map<string, string>>
    /** The dossier the user has open, set by ViewFile. Also covers dossiers that are not in the list. */
    currentFile?: FileReference
    setCurrentFile: (file?: FileReference) => void
    /** Synchronous lookup, so a screen that knows a file id never has to fetch /file/{id} for its basics. */
    getFile: (fileId?: string) => FileReference | undefined
    /** Documents of the dossier that is open; lives no longer than the visit to that dossier (HAM-4295). */
    documents: FileDocumentStore
    /**
     * How many notes the open dossier has, so the tab can show it (HAM-3476). Filled and kept up to date by the
     * note overview, which asks for this count on every reload anyway; undefined means "not counted yet".
     */
    noteCount?: number
    setNoteCount: (count?: number) => void
}

export function sortFileOptions<F extends FileListItemDto>(fileSort?: SortSetting) {
    return sortOptions<ComboBoxOption<F>>(
        fileSort?.direction === SortDirection.Ascending,
        (option: ComboBoxOption<F>) => option.data?.[fileSort?.field] || option.data.reference,
    )
}

export function mapFileOptions<F extends FileListItemDto>(fileSort?: SortSetting, idProp?: string) {
    return (file: F) => ({
        key: file[idProp] || file.reference,
        text: fileSort?.field === 'name' ? `${file.name} (${file.reference})` : `${file.reference} - ${file.name}`,
        data: file,
    })
}

const FileContext = createContext<FileContextType>({
    files: getDataEmptyState(),
    resetFiles: undefined,
    upsertFile: undefined,
    validateReferences: undefined,
    currentFile: undefined,
    setCurrentFile: undefined,
    getFile: undefined,
    documents: undefined,
    noteCount: undefined,
    setNoteCount: undefined,
})

export function FileProvider(props: PropsWithChildren): ReactElement {
    const axios = useAxios()
    // Re-fetch /file/list whenever the (user, environment) tuple changes. This is a defensive dependency.
    // See AuthorizationContext for the contract on `environmentKey`.
    const { environmentKey } = useContext(AuthorizationContext)
    const [files, , resetFiles] = useEntity<FileListItemDto[]>(
        () => ({ method: 'GET', url: '/file/list' }),
        false,
        result => result.data,
        [],
        undefined,
        [environmentKey],
    )

    const [currentFile, setCurrentFile] = useState<FileReference>()
    const [noteCount, setNoteCount] = useState<number>(0)
    // Created once; the store object is what the context hands out, so its identity never changes.
    const documents = useRef<FileDocumentStore>(undefined)
    if (!documents.current) {
        documents.current = createFileDocumentStore()
    }

    // Every dispatch of FileAction.ResetCurrent means the dossier context is gone: closing the dossier, landing on
    // a global overview or the dashboard, or useFileData switching to another file. The cached documents may not
    // survive that. Deliberately subscribed to outside of render rather than through useSelector: screens dispatch
    // this action from effects that re-run on every render of theirs, and re-rendering this provider on it would
    // put those screens in an endless render loop.
    const store = useStore<Store>()
    useEffect(() => {
        let previous = store.getState().files?.currentResetCount
        return store.subscribe(() => {
            const next = store.getState().files?.currentResetCount
            if (next === previous) {
                return
            }
            previous = next
            documents.current.invalidate()
            setCurrentFile(undefined)
            setNoteCount(undefined)
        })
    }, [store])

    useEffect(() => {
        documents.current.invalidate()
        setCurrentFile(undefined)
        setNoteCount(undefined)
    }, [environmentKey])

    return (
        <FileContext.Provider
            value={{
                files,
                currentFile,
                setCurrentFile,
                getFile: (fileId?: string) =>
                    fileId ? (currentFile?.id === fileId ? currentFile : files?.data?.find(file => file.id === fileId)) : undefined,
                documents: documents.current,
                noteCount,
                setNoteCount,
                resetFiles: () => resetFiles(),
                upsertFile: (file: FileListItemDto) => {
                    files.data = [...files?.data?.filter(item => item.id !== file.id), file]
                },
                validateReferences: (references: string[]) =>
                    references?.length > 0
                        ? axios
                              .get<{ [key: string]: string }>('/file/reference/validate', { params: { references } })
                              .then(response => new Map(Object.entries<string>(response.data)))
                        : Promise.resolve(new Map()),
            }}
        >
            {props.children}
        </FileContext.Provider>
    )
}

export type FileFolderTree = {
    /** The complete folder-only crawl of this dossier, or undefined when it has not been loaded yet. */
    folders: FolderDriveItem[] | undefined
    /** Persists what the combobox just crawled, so the next dialog in this dossier opens without Graph calls. */
    setFolders: (items: FolderDriveItem[]) => void
    /** Drops both the folder list and the document tree; a folder mutation invalidates both. */
    invalidate: () => void
}

/**
 * The folder cache of one dossier. Replaces the former FileFolderTreeContext, which lived inside ViewFile and was
 * therefore out of reach for the dialogs hosted by PopupProvider (HAM-4295).
 */
export function useFileFolderTree(documentRootId?: string): FileFolderTree {
    const { documents } = useContext(FileContext)

    const folders = useSyncExternalStore(
        listener => documents?.subscribe(listener) ?? (() => undefined),
        () => documents?.getFolders(documentRootId),
    )

    return useMemo(
        () => ({
            folders,
            setFolders: (items: FolderDriveItem[]) => documents?.setFolders(documentRootId, items),
            invalidate: () => documents?.invalidate(documentRootId),
        }),
        [documents, documentRootId, folders],
    )
}

export default FileContext
